import React from 'react';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import ArchitectureOutlinedIcon from '@mui/icons-material/ArchitectureOutlined';
import SpeedOutlinedIcon from '@mui/icons-material/SpeedOutlined';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import { motion } from 'framer-motion';
import setup from '../assets/data/setup.json';
import MotionSection from './ui/MotionSection';
import SectionHeader from './ui/SectionHeader';
import { TagIconRow } from './ui/TagIcons';

const iconMap: Record<string, React.ReactNode> = {
  adaptability: <AutoAwesomeIcon sx={{ fontSize: 22 }} />,
  ownership: <RocketLaunchOutlinedIcon sx={{ fontSize: 22 }} />,
  integrations: <HubOutlinedIcon sx={{ fontSize: 22 }} />,
  architecture: <ArchitectureOutlinedIcon sx={{ fontSize: 22 }} />,
  performance: <SpeedOutlinedIcon sx={{ fontSize: 22 }} />,
  security: <SecurityOutlinedIcon sx={{ fontSize: 22 }} />,
};

type Highlight = (typeof setup.engineeringHighlights)[number] & {
  stat?: string;
  tags?: string[];
};

function EngineeringHighlights() {
  const highlights = setup.engineeringHighlights as Highlight[];

  return (
    <MotionSection id="highlights">
      <SectionHeader
        eyebrow="Impact"
        title="Engineering Highlights"
        subtitle="What kind of engineer I am—backed by production systems, not buzzwords."
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {highlights.map((item, index) => (
          <motion.div
            key={item.title}
            initial={{ opacity: 0, y: 16, filter: 'blur(4px)' }}
            whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            viewport={{ once: true }}
            transition={{ duration: 0.45, delay: index * 0.05 }}
            whileHover={{ y: -3 }}
            className="ui-card ui-card-lift p-5 sm:p-6 group"
          >
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="w-11 h-11 rounded-lg border border-line bg-canvas-muted flex items-center justify-center text-ink-secondary group-hover:border-accent/30 transition-colors">
                {iconMap[item.icon] ?? iconMap.adaptability}
              </div>
              {item.stat && (
                <span className="text-xs font-semibold text-accent bg-accent-muted/60 px-2 py-1 rounded-md whitespace-nowrap">
                  {item.stat}
                </span>
              )}
            </div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-muted mb-1">{item.category}</p>
            <h3 className="text-[15px] font-semibold text-ink mb-2 tracking-[-0.01em]">{item.title}</h3>
            <p className="text-sm text-ink-muted leading-relaxed mb-3">{item.description}</p>
            {item.tags && item.tags.length > 0 && <TagIconRow tags={item.tags} />}
          </motion.div>
        ))}
      </div>
    </MotionSection>
  );
}

export default EngineeringHighlights;
